/**
 * CalendarSettings - Terminkalender-Grundeinstellungen
 * Extrahiert aus SettingsPage.js für bessere Wartbarkeit
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Calendar, Save } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarSettings = () => {
  const [calendarSettings, setCalendarSettings] = useState({
    slot_duration: '30',
    booking_window_days: 14,
    min_advance_hours: 6,
    cancellation_hours: 24,
    calendar_start_hour: 6,
    calendar_end_hour: 20,
    calendar_slot_height: 24,
    no_show_penalty_type: 'full',
    no_show_penalty_value: 0
  });
  const [loading, setLoading] = useState(false);

  const fetchCalendarSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCalendarSettings(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (error) {
      console.error('Error fetching calendar settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchCalendarSettings();
  }, [fetchCalendarSettings]);

  const handleSaveCalendarSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/calendar`, calendarSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Kalender-Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving calendar settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar size={24} />
              Terminkalender-Grundeinstellungen
            </CardTitle>
            <CardDescription>
              Konfigurieren Sie die Einstellungen für die Buchung von Kundenterminen
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Zeitslot-Dauer</Label>
            <Select 
              value={calendarSettings.slot_duration} 
              onValueChange={(value) => setCalendarSettings({...calendarSettings, slot_duration: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="15">15 Minuten</SelectItem>
                <SelectItem value="30">30 Minuten (Standard)</SelectItem>
                <SelectItem value="60">60 Minuten</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Standard-Dauer der Zeitslots im Kalender. Kann pro Service individuell überschrieben werden.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Buchungsfenster (Tage im Voraus)</Label>
            <Input
              type="number"
              value={calendarSettings.booking_window_days}
              onChange={(e) => setCalendarSettings({...calendarSettings, booking_window_days: e.target.value})}
            />
            <p className="text-xs text-gray-500">
              Wie viele Tage im Voraus können Kunden buchen?
            </p>
          </div>

          <div className="space-y-2">
            <Label>Mindest-Vorlaufzeit (Stunden)</Label>
            <Select 
              value={String(calendarSettings.min_advance_hours || 6)} 
              onValueChange={(value) => setCalendarSettings({...calendarSettings, min_advance_hours: parseInt(value)})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="0">Sofort buchbar</SelectItem>
                <SelectItem value="1">1 Stunde</SelectItem>
                <SelectItem value="2">2 Stunden</SelectItem>
                <SelectItem value="3">3 Stunden</SelectItem>
                <SelectItem value="4">4 Stunden</SelectItem>
                <SelectItem value="6">6 Stunden</SelectItem>
                <SelectItem value="12">12 Stunden</SelectItem>
                <SelectItem value="24">24 Stunden (1 Tag)</SelectItem>
                <SelectItem value="48">48 Stunden (2 Tage)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500">
              Wie viele Stunden im Voraus müssen Kunden mindestens buchen?
            </p>
          </div>

          <div className="space-y-2">
            <Label>Stornierungsfrist (Stunden)</Label>
            <Input
              type="number"
              value={calendarSettings.cancellation_hours}
              onChange={(e) => setCalendarSettings({...calendarSettings, cancellation_hours: e.target.value})}
            />
            <p className="text-xs text-gray-500">
              Mindestanzahl Stunden vor Termin für kostenlose Stornierung
            </p>
          </div>

          {/* Kalender-Zeiten */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Kalender Startzeit</Label>
              <Select 
                value={String(calendarSettings.calendar_start_hour || 6)} 
                onValueChange={(value) => setCalendarSettings({...calendarSettings, calendar_start_hour: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[4, 5, 6, 7, 8].map(h => (
                    <SelectItem key={h} value={String(h)}>{h.toString().padStart(2, '0')}:00 Uhr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Ab wann der Kalender startet</p>
            </div>
            <div className="space-y-2">
              <Label>Kalender Endzeit</Label>
              <Select 
                value={String(calendarSettings.calendar_end_hour || 20)} 
                onValueChange={(value) => setCalendarSettings({...calendarSettings, calendar_end_hour: parseInt(value)})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[18, 19, 20, 21, 22, 23].map(h => (
                    <SelectItem key={h} value={String(h)}>{h.toString().padStart(2, '0')}:00 Uhr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">Bis wann der Kalender geht</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Kalender Slot-Höhe (UI)</Label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="16"
                max="48"
                step="4"
                value={calendarSettings.calendar_slot_height || 24}
                onChange={(e) => setCalendarSettings({...calendarSettings, calendar_slot_height: parseInt(e.target.value)})}
                className="flex-1"
              />
              <span className="text-sm font-medium w-16 text-right">{calendarSettings.calendar_slot_height || 24}px</span>
            </div>
            <p className="text-xs text-gray-500">
              Höhe der Zeitslots im Kalender (kleiner = kompakter, weniger Scrollen)
            </p>
          </div>

          <div className="space-y-2">
            <Label>No-Show Strafe</Label>
            <Select 
              value={calendarSettings.no_show_penalty_type} 
              onValueChange={(value) => setCalendarSettings({...calendarSettings, no_show_penalty_type: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Volle Punkte abziehen</SelectItem>
                <SelectItem value="percentage">Prozentsatz abziehen</SelectItem>
                <SelectItem value="fixed">Fixe Punkte-Strafe</SelectItem>
                <SelectItem value="none">Keine Strafe</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {calendarSettings.no_show_penalty_type === 'percentage' && (
            <div className="space-y-2">
              <Label>Prozentsatz (%)</Label>
              <Input
                type="number"
                value={calendarSettings.no_show_penalty_value}
                onChange={(e) => setCalendarSettings({...calendarSettings, no_show_penalty_value: e.target.value})}
                placeholder="50"
              />
            </div>
          )}

          {calendarSettings.no_show_penalty_type === 'fixed' && (
            <div className="space-y-2">
              <Label>Fixe Punkte-Strafe</Label>
              <Input
                type="number"
                value={calendarSettings.no_show_penalty_value}
                onChange={(e) => setCalendarSettings({...calendarSettings, no_show_penalty_value: e.target.value})}
                placeholder="3"
              />
            </div>
          )}
        </div>

        <div className="pt-4 border-t flex justify-end">
          <Button onClick={handleSaveCalendarSettings} disabled={loading}>
            <Save size={16} className="mr-2" />
            Kalender-Einstellungen speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CalendarSettings;
